import * as _ from "lodash";
import * as React from "react";
import { useMappedState } from "redux-react-hook";
import styled, { css } from "styled-components";
import { capitalize } from "lodash";

import { Address } from "src/library/components/Address";
import { Datetime } from "src/library/components/Datetime";
import { InfoBar } from "src/library/components/InfoBar";
import { Link } from "src/library/components/Link";
import { Text } from "src/library/components/Text";
import { sentenceCase } from "src/library/utils/string-manipulations";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { Button } from "src/library/components/Button";
import { OrderTypeText } from "src/core/orders/components/OrderTypeText";
import OrderStatus from "src/core/orders/components/OrderStatus";
import {
  calculateCartTotalQuantity,
  calculatePriceTotals,
  totalWeight
} from "src/core/orders/utils/orders";
import { usePriceVisibility } from "src/core/prices/hooks/use-price-visibility";
import type { IStore } from "src/core/shared/store";
import {
  IOrder,
  IOrderLine
} from "src/core/orders/api/orders.api";
import { IDrop } from "src/core/drops/api/drops.api";
import { OrderPaymentStatusText } from "src/modules/payment/components";
import { formatPrice } from "src/core/prices/utils";
import {
  TransactionType,
  TransactionStatus
} from "src/modules/payment/api/transactions.api";
import ErpSyncStatus from "src/modules/connector/components/ErpSyncStatus";
import { useWindowSize } from "src/library/hooks";
import { roundOrToFixed } from "src/library/utils";

import { orderReferenceNumber } from "../utils/order-reference-number";
import {
  OrderMode,
  OrderOrigin
} from "../api/orders.api";
import { packSizeConstraints } from "../utils/pack-size-constraints";
import OrderVariantLabel from "./orderVariantLabel";

interface IProps {
  isLoading: boolean;
  viewMode: OrderMode;
  data: IOrder | null;
  orderMode?: OrderMode | null;
  primaryAction?: JSX.Element | null;
  secondaryAction?: JSX.Element | null;
  terminology: ITerminology;
  drop: IDrop;
  onTransactionClick?: () => void;
}

/**
 * Order information sidebar
 */
const OrderInfoSidebar: React.ReactFCWithChildren<IProps> = ({
  isLoading,
  data,
  primaryAction,
  secondaryAction,
  terminology,
  drop,
  onTransactionClick
}) => {
  const {
    currentOrder, roleType, installedModules, packSizeConstraintsSettings, orderVariants
  } = useMappedState(({ orders, global }: IStore) => ({
    currentOrder: orders.currentOrder,
    roleType: global.role ? global.role.type : null,
    installedModules: global.settings?.installedModules || [],
    preauth: global.settings?.payment?.preauthorizationsEnabled,
    packSizeConstraintsSettings: global?.settings?.packSizeConstraints ?? [],
    orderVariants: global.settings?.orderVariants || []
  }));

  const pricesVisible = usePriceVisibility();
  const { width } = useWindowSize();

  const totalPaid = React.useMemo(() => {
    if (!installedModules.includes("payment")) {
      return null;
    }

    if (!data?.transactions?.length) {
      return null;
    }

    return _.reduce(data?.transactions, (prev, currentTransaction) => {
      if (![ TransactionStatus.SUCCEEDED, TransactionStatus.REQUIRES_CAPTURE ].includes(currentTransaction.status)) {
        return prev;
      }

      if (currentTransaction.type === TransactionType.CHARGE) {
        return prev + currentTransaction.amount;
      } else {
        return prev - currentTransaction.amount;
      }
    }, 0);
  }, [ data?.transactions, installedModules ]);

  const totalOrderWeight = React.useMemo(() => {
    // if its covered by the constraints then dont render the weight
    if (packSizeConstraintsSettings.find(psc => psc.code === "weight")) {
      return null;
    }

    return totalWeight(currentOrder.data);
  }, [ currentOrder.data, packSizeConstraintsSettings ]);

  /** if pack size constraints lets display the relevant info */
  const packSizeConstraintsToShow = React.useMemo(() => {
    if (data?.branch?.groupedSettings) {
      const result = packSizeConstraints(_.uniqBy(packSizeConstraintsSettings, "code"), data.orderLines);

      return result.map(constraint => {
        const quantity = roundOrToFixed(constraint.quantity, 2);

        if (quantity <= 0) {
          return null;
        }

        return {
          label: constraint.code === "weight" ? "Total Weight" : capitalize(constraint.plural ?? constraint.code),
          value: (
            <Text>
              {`${quantity}${constraint.code === "weight" ? "kg" : ""}`}
            </Text>
          )
        };
      });
    }

    return [];
  }, [
    data?.branch?.groupedSettings,
    data?.orderLines,
    packSizeConstraintsSettings
  ]);

  const calculatedCartTotal = React.useCallback((orderLines: IOrderLine[]) => {
    return calculatePriceTotals(orderLines || [], drop);
  }, [ drop ]);

  const items = React.useMemo(() => {
    let sidebarItems = _.compact([
      (currentOrder.mode === "view" || data?.origin === OrderOrigin.Prediction) && {
        label: "Purchase Order No.",
        value: (
          <Text>
            {data && data.customerReference ? data.customerReference : "-"}
          </Text>
        )
      },
      {
        label: "Status",
        value: data ? (
          <OrderStatus
            orderId={data.id}
            status={data.orderStatus}
            tooltipPlace="left"
          />
        ) : "-"
      },
      data?.orderVariant && orderVariants.length > 1 && {
        label: terminology.orderVariant?.singular.text ?? "Order Variant",
        value: <OrderVariantLabel
          orderVariant={data?.orderVariant}
          place="left"
               />
      },
      {
        label: "Total quantity",
        value: data && data.orderLines ? calculateCartTotalQuantity(data.orderLines) : "-"
      },
      drop && pricesVisible ? {
        label: "Order total",
        value: data ? (
          <StyledPricesWrapper>
            <Text className="total-price">

              <Text className="price-value">
                {formatPrice(calculatedCartTotal(data.orderLines).total + (data.deliveryCharge?.price || 0), 2, drop.priceCode?.currency)}
              </Text>
            </Text>

            <Text>
              <strong>
                Items:
              </strong>

              <Text className="price-value">
                {formatPrice(calculatedCartTotal(data.orderLines).net, 2, drop.priceCode?.currency)}
              </Text>
            </Text>

            <Text>
              <strong>
                VAT:
              </strong>

              <Text className="price-value">
                {formatPrice(calculatedCartTotal(data.orderLines).vat, 2, drop.priceCode?.currency)}
              </Text>
            </Text>

            {data.deliveryCharge && (
              <>
                <Text>
                  <strong>
                    Delivery charge:
                  </strong>

                  <Text className="price-value">
                    {formatPrice(data.deliveryCharge.price, 2, drop.priceCode?.currency)}
                  </Text>
                </Text>

              </>
            )}

          </StyledPricesWrapper>
        ) : "-"
      } : null,

      totalPaid !== null ?
        {
          label: "Total paid",
          value: (
            <Text>
              {data && data.customerReference ? data.customerReference : "-"}
            </Text>
          )
        } : null,
      data?.paymentStatus ? {
        label: "Payment status",
        value: <OrderPaymentStatusText status={data.paymentStatus} />
      } : null,
      ...packSizeConstraintsToShow,
      totalOrderWeight !== null ? {
        label: "Total weight",
        value: (
          <Text>
            {`${totalOrderWeight}kg`}
          </Text>
        )
      } : null,
      {
        label: "Delivery date",
        value: data && data.orderDeliveryDate ? (
          <Datetime
            ignoreLocal
            template="long_date"
            date={data.orderDeliveryDate}
          />
        ) : "-"
      },

      totalPaid !== null ?
        {
          label: "Total paid",
          value: (
            <TotalPaidWrapper>
              <Button
                type="button"
                $buttonType="plain"
                size="micro"
                onClick={onTransactionClick}
              >
                (view payments)
              </Button>

              <Text>
                {`${formatPrice(totalPaid, 2, drop.priceCode?.currency)}`}
              </Text>

            </TotalPaidWrapper>)
        } :
        null,
      data ? {
        label: "Order Number",
        value: `#${orderReferenceNumber(data, installedModules)}`
      } : null,

      data?.invoiceNumber ? {
        label: "Invoice Number",
        value: data && data.invoiceNumber ? data.invoiceNumber : "-"
      } : null,
      data?.invoiceDate ? {
        label: "Invoice Date",
        value: data && data.invoiceDate ? (
          <Datetime
            template="long_date"
            date={data.invoiceDate}
          />
        ) : "-"
      } : null,

      data?.list?.name ? {
        label: "List",
        value:
         (
           <Link
             color="primary"
             textTransform="capitalize"
             type="underlined"
             to={`/lists/${data.list.id}`}
           >
             {data.list.name}
           </Link>
         )
      } : null,
      {
        label: `${terminology.order.singular.text} type`,
        value: data ? (
          data.type === "scheduled" ? (
            <>
              {data?.list?.id ? (
                <Link
                  color="primary"
                  textTransform="capitalize"
                  type="underlined"
                  to={`/lists/${data?.list?.id}`}
                >
                  {_.startCase(terminology.schedule.singular.text)}
                </Link>
              ) : (
                <Text>
                  {_.startCase(terminology.schedule.singular.text)}
                </Text>
              )}
            </>
          ) : data.type === "bulk" ? (
            <>
              <OrderTypeText type={data.type} />

              {roleType === "admin" && (
                <>
                  <br />

                  {_.map(data.imports, orderImportJob => (
                    <>
                      <Link
                        color="primary"
                        textTransform="capitalize"
                        type="underlined"
                        to={`/admin/orders/imports/${orderImportJob.id}`}
                      >
                        <Datetime
                          date={orderImportJob.addedAt}
                          template="datetime"
                        />

                        {" "}
                        (View)
                      </Link>

                      <br />
                    </>
                  ))}
                </>
              )}
            </>
          ) : (
            <OrderTypeText type={data.type} />
          )
        ) : (
          "-"
        )
      },
      {
        label: `${terminology.customer.singular.text}`,
        value: data?.branch?.customer ? roleType === "admin" ? (
          <Link
            color="primary"
            textTransform="capitalize"
            type="underlined"
            to={`/admin/customers/${data?.branch?.customerId}/drops`}
          >
            {`${data.branch.customer.name} (${data.branch.customer.accountNumber})`}
          </Link>
        ) : `${data.branch.customer.name} (${data.branch.customer.accountNumber})` : "-"
      },
      {
        label: `${terminology.drop.singular.text}`,
        value: data?.branch ? roleType === "admin" ? (
          <Link
            color="primary"
            textTransform="capitalize"
            type="underlined"
            to={`/admin/customers/${data?.branch?.customerId}/drops/${data?.branchId}/orders`}
          >
            {`${data.branch?.name} (${data.branch.branchNumber})`}
          </Link>
        ) : `${data.branch?.name} (${data.branch.branchNumber})` : "-"
      },
      {
        label: "Delivery Address",
        value: data && data.branch ? <Address address={data.branch.address} /> : "-"
      },
      data?.orderPlacedDate ? {
        label: "Ordered On",
        value: <Datetime
          template="long_date"
          date={data.orderPlacedDate}
               />
      } : null
    ]);

    if (roleType === "admin") {
      if ((
        installedModules?.includes("rjl") ||
        installedModules?.includes("unity") ||
        installedModules?.includes("odoo") ||
        installedModules?.includes("zertus")
      ) && roleType === "admin") {
        sidebarItems.push({
          label: "API status",
          value: <ErpSyncStatus status={data?.erpSyncStatus} />
        });
      }

      if (installedModules?.includes("rjl")) {
        sidebarItems.push({
          label: "RJL delivery number",
          value: data?.rjlDeliveryNumber || "-"
        });
      }
    }

    if (data?.customData) {
      sidebarItems = sidebarItems.concat(_.compact(_.map(data.customData, customDataRow => {
        if (roleType === "customer" && customDataRow.admin) {
          return null;
        }

        return ({
          label: customDataRow.description,
          value: customDataRow.value
        });
      }
      )));
    }

    return sidebarItems;
  }, [
    currentOrder.mode,
    data,
    orderVariants.length,
    drop,
    pricesVisible,
    calculatedCartTotal,
    totalPaid,
    packSizeConstraintsToShow,
    totalOrderWeight,
    onTransactionClick,
    installedModules,
    terminology,
    roleType
  ]);

  return (
    <InfoBar
      isLoading={isLoading}
      title={`${sentenceCase(terminology.order.singular.text)} Information`}
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
      items={items}
      hideInformation={currentOrder.mode === "edit" && (width ? width < 1000 : false)}
    />
  );
};

export default OrderInfoSidebar;

const TotalPaidWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StyledPricesWrapper = styled.div`
  ${({ theme }) => css`
      display: block;
      
      > span:first-child {
        display: block;
      }

     > span {
      display: block;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
     }

    strong {
      text-align: left;
      font-weight: ${theme.fonts.weights.bold};
      width: 130px; 
    }

    .price-value {
      display: inline-block;
      text-align: right;
    }

    .total-price {
      border-bottom: 2px solid ${theme.colors.lightGrey};
      padding-bottom: ${theme.spacing.padding.md};
      margin-bottom: ${theme.spacing.padding.md};
    }
  `}
`;
